import { useState, type CSSProperties } from 'react';
import { AiqMark } from './AiqMark';
import { LearningAIQVisual } from './LearningAIQVisual';
import './learning-score-button.css';
import { WorkspaceDialog } from './WorkspaceDialog';
import { AIQ_BASE, AIQ_MAX } from './learning-aiq';
import type { jobLearningAIQ } from './job-learning-aiq';

/** The job's AIQ. `compact` sits in a row of buttons; either way it opens the full explanation. */
export function LearningAIQ({
  measurement,
  compact = false,
}: {
  measurement: ReturnType<typeof jobLearningAIQ>;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const score = measurement?.score ?? AIQ_BASE;
  const position = (score - AIQ_BASE) / (AIQ_MAX - AIQ_BASE);
  return (
    <div className={`learning-aiq ${compact ? 'is-compact' : ''}`}>
      <button
        type="button"
        className="aiq-explanation-trigger"
        aria-label={`About AIQ ${score}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {compact ? (
          <span className="aiq-compact">
            <AiqMark size={22} />
            <strong>{score}</strong>
            <span className="aiq-compact-label">
              <b>AIQ</b>
              <i aria-hidden="true">
                <i style={{ width: `${Math.max(4, position * 100)}%` }} />
              </i>
            </span>
          </span>
        ) : (
          <LearningAIQVisual score={measurement?.score ?? null} />
        )}
      </button>
      {open && (
        <WorkspaceDialog
          telemetry="aiq"
          title="Your job’s AIQ"
          compact
          onClose={() => setOpen(false)}
        >
          <div className="aiq-guide">
            <div className="aiq-guide-intro">
              <div className="aiq-guide-hero">
                <LearningAIQVisual score={measurement?.score ?? null} />
              </div>
              <div className="aiq-guide-copy">
                <h3>
                  You teach it.
                  <br />
                  It gets smarter.
                </h3>
                <p>
                  Your confirmations build knowledge. AIQ rises as that knowledge supports learned
                  skills, with full credit when they get new documents right. Not every confirmation
                  changes the rounded score. Pages waiting do not affect it; new kinds of work can.
                </p>
              </div>
            </div>
            <div
              className="aiq-scale"
              aria-label={`AIQ scale: ${AIQ_BASE} to ${AIQ_MAX}. Current score ${score}.`}
            >
              <div
                className="aiq-scale-track"
                style={{ '--aiq-position': `${position * 100}%` } as CSSProperties}
              >
                <i className="aiq-scale-earned" />
                <span />
              </div>
              <div className="aiq-scale-labels">
                <span>
                  <b>80</b>Starting point
                </span>
                <span>
                  <b>120</b>Building knowledge
                </span>
                <span>
                  <b>165</b>Top of the scale
                </span>
              </div>
            </div>
          </div>
        </WorkspaceDialog>
      )}
    </div>
  );
}
